import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Mail, RefreshCw, Send, Users, Clock, Activity, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const Stat = ({ label, value, sub }) => (
    <Card className="bg-paper border-border p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">{label}</div>
        <div className="mt-2 text-3xl font-heading font-black text-white">{value}</div>
        {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </Card>
);

const ForYouPulse = () => {
    const { token } = useAuth();
    const authH = () => ({ Authorization: `Bearer ${token}` });

    const [rows, setRows] = useState([]);
    const [sentToday, setSentToday] = useState(0);
    const [sentWeek, setSentWeek] = useState(0);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [lastRun, setLastRun] = useState(null);
    const [profilePhone, setProfilePhone] = useState(null);
    const [region, setRegion] = useState("all");
    const [regions, setRegions] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [logRes, regRes] = await Promise.all([
                axios.get(`${API}/admin/sms/for-you/log?limit=200`, { headers: authH() }),
                axios.get(`${API}/admin/customers/regions`, { headers: authH() }).catch(() => ({ data: [] })),
            ]);
            setRows(logRes.data.rows || []);
            setSentToday(logRes.data.sent_today || 0);
            setSentWeek(logRes.data.sent_last_7d || 0);
            setRegions(regRes.data || []);
        } catch {
            toast.error("Failed to load for-you log");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    const runBatch = async (force) => {
        setRunning(true);
        try {
            const params = new URLSearchParams();
            if (force) params.set("force", "true");
            if (region && region !== "all") params.set("region", region);
            const { data } = await axios.post(
                `${API}/admin/sms/for-you/run-now${params.toString() ? "?" + params.toString() : ""}`,
                null,
                { headers: authH() }
            );
            setLastRun(data);
            const where = data.region ? ` in ${data.region}` : "";
            toast.success(`Batch${where} done — ${data.sent} sent, ${data.skipped_duplicate} dup, ${data.skipped_no_content} no-content`);
            fetchData();
        } catch (e) {
            toast.error("Batch failed: " + (e?.response?.data?.detail || e.message));
        } finally { setRunning(false); }
    };

    return (
        <AdminLayout title="For-You Pulse">
            <div className="space-y-6" data-testid="for-you-pulse-page">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-heading text-2xl font-black flex items-center gap-2">
                            <Sparkles className="h-6 w-6 text-electric" />
                            Personalised SMS Pulse
                        </h2>
                        <p className="text-sm text-white/60 mt-1 max-w-2xl">
                            Swahili digest auto-dispatched <span className="text-electric font-semibold">daily at 07:00 EAT</span> to every active customer,
                            picking the 1 article + 1 upcoming prediction most relevant to their questions and team subscriptions. Capped at 160 characters.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 bg-obsidian border border-border rounded-sm px-2 py-1">
                            <MapPin className="h-3.5 w-3.5 text-electric" />
                            <Select value={region} onValueChange={setRegion}>
                                <SelectTrigger className="bg-transparent border-0 text-white text-xs h-7 w-40 px-1" data-testid="region-select">
                                    <SelectValue placeholder="All regions" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All regions</SelectItem>
                                    {regions.map((r) => (
                                        <SelectItem key={r.region} value={r.region}>{r.region} ({r.count})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            variant="outline"
                            onClick={fetchData}
                            disabled={loading}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                        <Button
                            onClick={() => runBatch(false)}
                            disabled={running}
                            className="bg-electric hover:bg-electric/90"
                            data-testid="run-now-btn"
                        >
                            <Send className="h-4 w-4 mr-2" />
                            {running ? "Running…" : (region !== "all" ? `Run for ${region}` : "Run now")}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => runBatch(true)}
                            disabled={running}
                            data-testid="run-force-btn"
                            title="Bypass once-per-day idempotency check"
                        >
                            Force re-send
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Sent today" value={sentToday} sub="Unique phones" />
                    <Stat label="Last 7 days" value={sentWeek} />
                    <Stat
                        label="Last batch sent"
                        value={lastRun ? lastRun.sent : "—"}
                        sub={lastRun ? `${lastRun.eligible} eligible · ${lastRun.skipped_no_content} no-match` : "Click Run now"}
                    />
                    <Stat
                        label="Errors"
                        value={lastRun ? lastRun.errors : 0}
                        sub={lastRun?.errors ? "Investigate" : "All clear"}
                    />
                </div>

                <Card className="bg-paper border-border p-0 overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        <span className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                            <Mail className="h-4 w-4" /> Sent digests <span className="text-white/40">({rows.length})</span>
                        </span>
                    </div>
                    <div className="divide-y divide-border max-h-[640px] overflow-y-auto" data-testid="digest-list">
                        {loading && <div className="p-10 text-center text-white/50">Loading…</div>}
                        {!loading && rows.length === 0 && (
                            <div className="p-10 text-center text-white/50">No digests sent yet. Click <span className="text-electric">Run now</span> above to dispatch the first batch.</div>
                        )}
                        {!loading && rows.map((r) => (
                            <div key={r.id} className="p-4 hover:bg-subtle/40" data-testid={`digest-row-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => setProfilePhone(r.phone_number)}
                                            className="font-mono text-xs text-white/80 hover:text-electric underline-offset-2 hover:underline"
                                            data-testid={`open-profile-${r.phone_number}`}
                                            title="Open customer profile"
                                        >
                                            {r.phone_number}
                                        </button>
                                        {r.delivered ? (
                                            <Badge className="bg-emerald-500/15 text-emerald-300 border-0 text-[10px]">Delivered</Badge>
                                        ) : (
                                            <Badge className="bg-yellow-500/15 text-yellow-300 border-0 text-[10px]">Pending</Badge>
                                        )}
                                        {r.force && (
                                            <Badge variant="outline" className="border-electric/40 text-electric text-[10px]">force</Badge>
                                        )}
                                        {(r.priority_terms || []).slice(0, 3).map((t) => (
                                            <Badge key={t} className="bg-electric/20 text-electric border-0 text-[10px]">{t}</Badge>
                                        ))}
                                    </div>
                                    <span className="text-xs text-white/40 flex items-center gap-1">
                                        <Clock className="h-3 w-3" /> {fmt(r.sent_at)}
                                    </span>
                                </div>
                                <div className="text-sm text-white/90 leading-relaxed">{r.message}</div>
                                <div className="text-[10px] text-white/40 mt-1.5 font-mono">{r.character_count} / 160 chars · day {r.day}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

export default ForYouPulse;
